import { useEffect, useMemo, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import RecordForm from './components/RecordForm';
import RecordList from './components/RecordList';
import { COLUMNS, FIELDS, ORDER_TRANSITIONS, READ_ONLY_SECTIONS, SECTIONS, emptyDraft, readLookup, unwrap } from './consoleConfig';

const { contains } = window.ApperSDK.CoreSDK;
const TABLE_CLIENTS = {
  products_c: sdk.table('products_c'),
  productvariants_c: sdk.table('productvariants_c'),
  inventory_c: sdk.table('inventory_c'),
  orders_c: sdk.table('orders_c'),
  customers_c: sdk.table('customers_c'),
  discounts_c: sdk.table('discounts_c'),
  storesettings_c: sdk.table('storesettings_c'),
  returnrequests_c: sdk.table('returnrequests_c'),
};

export const route = { path: '/admin', layout: 'owner', access: "profile === 'Admin_c'" };
export const nav = { icon: 'LayoutDashboard', label: 'Store operations', section: 'Manage', order: 10, profiles: ['Admin_c'] };

export default function StoreConsole() {
  const [sectionKey, setSectionKey] = useState('products');
  const [search, setSearch] = useState('');
  const [formMode, setFormMode] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const section = SECTIONS.find((item) => item.key === sectionKey) || SECTIONS[0];
  const fields = FIELDS[section.table] || [];
  const canManage = !READ_ONLY_SECTIONS.has(section.key);
  const canCreate = canManage && fields.length > 0;

  useEffect(() => { document.title = section.label + ' | Bluewell Ventures'; }, [section.label]);

  const { data, loading, error, run } = useFetch(() => {
    let query = TABLE_CLIENTS[section.table].select(COLUMNS[section.table]).orderByDesc('CreatedOn').page(1, 100);
    if (search.trim()) query = query.where(contains('Name', search.trim()));
    return query.fetch().then(unwrap);
  }, [section.table, search]);

  const { data: references } = useFetch(async () => {
    const [products, variants] = await Promise.all([
      TABLE_CLIENTS.products_c.select(['Name', 'publication_status_c']).orderBy('Name').limit(100).fetch(),
      TABLE_CLIENTS.productvariants_c.select(['Name', 'pack_size_c', 'product_c']).orderBy('Name').limit(100).fetch(),
    ]);
    return { products: unwrap(products), productvariants: unwrap(variants) };
  }, []);

  const records = data ?? [];
  const choices = { products_c: references?.products ?? [], productvariants_c: references?.productvariants ?? [] };
  const summary = useMemo(() => ({
    count: records.length,
    attention: sectionKey === 'inventory'
      ? records.filter((item) => Number(item.on_hand_c || 0) <= Number(item.low_stock_threshold_c || 0)).length
      : sectionKey === 'orders'
        ? records.filter((item) => ['Pending Review', 'Payment Verified'].includes(item.order_status_c)).length
        : 0,
  }), [records, sectionKey]);

  function closeForm() {
    setFormMode('');
    setSelectedRow(null);
    setDraft({});
  }

  function beginCreate() {
    if (!canCreate) return;
    setDraft(emptyDraft(fields));
    setSelectedRow(null);
    setFormMode('create');
  }

  function beginEdit(row) {
    setDraft(Object.fromEntries(fields.map((field) => [field.key, field.type === 'lookup' ? readLookup(row[field.key]) : (row[field.key] ?? '')])));
    setSelectedRow(row);
    setFormMode('edit');
  }

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field.key]: value }));
  }

  async function saveRecord(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...draft };
      for (const field of fields) {
        if (field.type === 'number' && payload[field.key] !== '') payload[field.key] = Number(payload[field.key]);
        if (field.type === 'checkbox') payload[field.key] = Boolean(payload[field.key]);
        if (field.type === 'locked') payload[field.key] = false;
      }
      if (section.table === 'productvariants_c' && (payload.price_c < 400 || payload.price_c > 4000)) throw new Error('Variant prices must be between ₹400 and ₹4,000.');
      if (section.table === 'inventory_c') {
        if ([payload.on_hand_c, payload.reserved_c, payload.low_stock_threshold_c].some((value) => Number(value) < 0)) throw new Error('Stock quantities cannot be negative.');
        if (Number(payload.reserved_c) > Number(payload.on_hand_c)) throw new Error('Reserved stock cannot exceed stock on hand.');
      }
      if (section.table === 'discounts_c') {
        if (payload.value_c <= 0) throw new Error('Enter a discount greater than zero.');
        if (payload.discount_type_c === 'Percent' && payload.value_c > 100) throw new Error('A percentage discount cannot exceed 100%.');
      }
      if (section.table === 'storesettings_c') {
        if (payload.shipping_fee_c < 0 || payload.free_shipping_threshold_c < 0) throw new Error('Shipping amounts cannot be negative.');
        payload.cod_enabled_c = false;
        payload.returns_enabled_c = false;
      }
      const response = formMode === 'edit'
        ? await TABLE_CLIENTS[section.table].update({ Id: selectedRow.Id, ...payload })
        : await TABLE_CLIENTS[section.table].create(payload);
      if (!response?.data?.length) throw new Error(response?.messages?.[0] || response?.message || 'No record was saved.');
      toast.success(formMode === 'edit' ? 'Changes saved.' : 'Record added.');
      closeForm();
      await run();
    } catch (saveError) {
      toast.error(saveError.message || 'Could not save this record.');
    } finally {
      setBusy(false);
    }
  }

  async function saveOrderStatus(row, nextStatus) {
    if (!nextStatus || nextStatus === row.order_status_c || busy) return;
    if (!(ORDER_TRANSITIONS[row.order_status_c] || []).includes(nextStatus)) return toast.error('That order status change is not allowed.');
    setBusy(true);
    try {
      const response = await TABLE_CLIENTS.orders_c.update({ Id: row.Id, order_status_c: nextStatus });
      if (!response?.data?.length) throw new Error(response?.messages?.[0] || response?.message || 'Order status was not saved.');
      toast.success('Order fulfilment status updated.');
      await run();
    } catch (saveError) {
      toast.error(saveError.message || 'Could not update this order.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Bluewell Ventures / Owner</p><h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{section.label}</h1><p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">{section.description}</p></div>
        {canCreate && <Button onClick={beginCreate} className="min-h-11 shrink-0"><ApperIcon name="Plus" size={17} />Add {section.key === 'products' ? 'product' : section.key === 'variants' ? 'pack variant' : section.key === 'inventory' ? 'stock record' : section.key === 'offers' ? 'discount' : 'settings'}</Button>}
      </header>

      <nav aria-label="Store sections" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2">{SECTIONS.map((item) => <button type="button" key={item.key} onClick={() => { setSectionKey(item.key); setSearch(''); closeForm(); }} className={'flex min-h-11 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition-colors ' + (item.key === sectionKey ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-accent')}><ApperIcon name={item.icon} size={16} />{item.label}</button>)}</nav>
      {sectionKey === 'returns' && <div className="flex gap-4 rounded-xl border border-warning-border bg-warning-muted p-4 text-warning-foreground"><ApperIcon name="ShieldAlert" size={20} className="mt-0.5 shrink-0" /><div><p className="font-semibold">Return intake is not enabled.</p><p className="mt-1 text-sm leading-6">Approve a written return and refund policy before collecting requests. This section is reserved for the future customer flow.</p></div></div>}
      {sectionKey === 'orders' && <div className="flex gap-4 rounded-xl border border-border bg-card p-4"><ApperIcon name="LockKeyhole" size={20} className="mt-0.5 shrink-0 text-primary" /><p className="text-sm leading-6 text-muted-foreground">Order totals, addresses and payment status are read-only. Payment status must come from a verified gateway event. Orders appear after server-side checkout is connected.</p></div>}
      {sectionKey === 'customers' && <div className="flex gap-4 rounded-xl border border-border bg-card p-4"><ApperIcon name="ShieldCheck" size={20} className="mt-0.5 shrink-0 text-primary" /><p className="text-sm leading-6 text-muted-foreground">Customer records are private owner data. Signup and phone verification remain off until an OTP provider is configured.</p></div>}

      {sectionKey !== 'returns' && <>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><div className="flex items-center gap-3"><span className="font-heading text-2xl font-semibold tabular-nums">{summary.count}</span><span className="text-sm text-muted-foreground">{section.label.toLowerCase()} in this view</span>{summary.attention > 0 && <span className="rounded-full bg-warning-muted px-3 py-1 text-xs font-semibold text-warning-foreground">{summary.attention} need attention</span>}</div><div className="relative w-full sm:max-w-xs"><ApperIcon name="Search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input aria-label={'Search ' + section.label.toLowerCase()} placeholder="Search by name…" value={search} onChange={(event) => setSearch(event.target.value)} className="min-h-11 pl-9" /></div></div>

        {formMode && canManage && <RecordForm fields={fields} draft={draft} choices={choices} mode={formMode} name={formMode === 'edit' ? selectedRow?.Name : 'Add ' + section.label.toLowerCase().replace(/s$/, '')} busy={busy} onChange={updateDraft} onClose={closeForm} onSubmit={saveRecord} />}
        {loading && data === undefined && <div className="grid gap-3">{[0, 1, 2, 3].map((item) => <div key={item} className="h-16 animate-pulse rounded-xl bg-muted" />)}</div>}
        {error && <div role="alert" className="rounded-xl border border-destructive/30 bg-card p-5"><p className="font-semibold">Could not load {section.label.toLowerCase()}.</p><p className="mt-1 text-sm text-muted-foreground">{error}</p><Button variant="outline" className="mt-4 min-h-11" onClick={() => run()}>Try again</Button></div>}
        {!loading && !error && records.length === 0 && !formMode && <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-12 text-center"><span className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted text-primary"><ApperIcon name={section.icon} size={21} /></span><h2 className="mt-4 font-heading text-2xl font-semibold">No {section.label.toLowerCase()} yet</h2><p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-muted-foreground">{canCreate ? 'Add real store information when it is ready. No example business records have been inserted.' : 'Records appear here after verified customer and checkout flows are integrated.'}</p>{canCreate && <Button onClick={beginCreate} className="mt-5 min-h-11">Add the first record</Button>}</div>}
        {!loading && !error && records.length > 0 && <RecordList records={records} columns={COLUMNS[section.table]} sectionKey={section.key} allowEdit={['products', 'variants', 'inventory', 'offers', 'settings'].includes(section.key)} busy={busy} onEdit={beginEdit} onOrderStatus={saveOrderStatus} />}
      </>}
    </div>
  );
}